import { useEffect, useRef } from 'react';
import { clearDashboardAccountState } from './accountStorage';

/**
 * Single-active-session guard.
 *
 * The browser has ONE shared cookie jar, so logging in as a different account
 * in another tab (even on a different dashboard) silently overwrites the
 * session every other tab relies on. This hook makes every authenticated tab
 * notice that takeover and log itself out automatically — regardless of which
 * account/dashboard performed the new login.
 *
 * Detection works on two signals:
 *  1. `storage` events — a login in any other tab writes the new identity to
 *     localStorage (see accountStorage.setDashboardAccountState), which fires a
 *     storage event in *this* tab instantly.
 *  2. A periodic / on-focus `whoami` check — authoritative fallback that also
 *     covers HttpOnly production cookies.
 *
 * This tab's own identity is read from sessionStorage, which is per-tab and was
 * set when *this* tab logged in. The tab that performed the new login matches
 * its own fresh sessionStorage, so it never logs itself out — only the stale
 * tabs do.
 */

const IDENTITY_KEYS = ['activeAccountEmail', 'activeAccountId', 'guestEmail'];

const readSession = (key: string): string | null => {
  try { return sessionStorage.getItem(key); } catch (e) { return null; }
};

const readLocal = (key: string): string | null => {
  try { return localStorage.getItem(key); } catch (e) { return null; }
};

/** This tab's identity, captured per-tab in sessionStorage at login time. */
const getTabIdentity = (): { email: string | null; id: string | null } => {
  const email = (readSession('activeAccountEmail') || readSession('guestEmail') || '').trim().toLowerCase() || null;
  const id = (readSession('activeAccountId') || '').trim() || null;
  return { email, id };
};

const isLoggedInTab = (): boolean => {
  const { email, id } = getTabIdentity();
  return !!(email || id);
};

async function serverIdentityDiffers(tabEmail: string | null): Promise<boolean> {
  // Only email is reliable here: whoami returns the underlying User._id, while
  // this tab's activeAccountId is the organizer/bar id — so ids legitimately
  // differ and must NOT be compared. The email matches across both.
  if (!tabEmail) return false;
  try {
    const resp = await fetch('/api/auth/whoami', { method: 'GET', credentials: 'include' });
    const who = await resp.json().catch(() => null);
    if (!who) return false;
    // IMPORTANT: only force a logout when the server reports a *different, logged-in*
    // account — that is the genuine "another tab took over the session" case.
    //
    // `loggedIn: false` is intentionally treated as UNCERTAIN (like a network hiccup)
    // and must NOT trigger a logout: whoami can transiently return false right after
    // login (cookie not yet propagated) or on a brief DB read failure server-side.
    // Doing otherwise kicks a freshly logged-in bar/organizer to the guest login.
    if (!who.loggedIn) return false;
    const serverEmail = who.user && who.user.email ? String(who.user.email).trim().toLowerCase() : null;
    if (serverEmail && serverEmail !== tabEmail) return true;
    return false;
  } catch (e) {
    // Network hiccup → don't log out on uncertainty.
    return false;
  }
}

export function useSessionGuard() {
  const loggingOut = useRef(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const forceLogout = async (reason: string) => {
      if (loggingOut.current) return;
      loggingOut.current = true;
      try { console.warn('[sessionGuard] forcing logout:', reason); } catch (e) {}
      try { clearDashboardAccountState(); } catch (e) {}
      try { await fetch('/api/auth/logout', { method: 'POST', credentials: 'include' }); } catch (e) {}
      // Full reload to /login so all in-memory React state is dropped.
      try { window.history.pushState({}, '', '/login?reason=session_replaced'); } catch (e) {}
      try { window.dispatchEvent(new PopStateEvent('popstate')); } catch (e) {}
    };

    const check = async () => {
      if (loggingOut.current) return;
      if (!isLoggedInTab()) return;
      const { email, id } = getTabIdentity();

      // Fast local check: another tab's login overwrote the shared identity.
      const localEmail = (readLocal('activeAccountEmail') || '').trim().toLowerCase() || null;
      const localId = (readLocal('activeAccountId') || '').trim() || null;
      if (email && localEmail && localEmail !== email) { await forceLogout('localStorage email mismatch'); return; }
      if (id && localId && localId !== id) { await forceLogout('localStorage id mismatch'); return; }

      // Authoritative server check (email-based).
      if (await serverIdentityDiffers(email)) { await forceLogout('server identity differs'); }
    };

    const onStorage = (e: StorageEvent) => {
      if (e.key && !IDENTITY_KEYS.includes(e.key)) return;
      check();
    };
    const onVisibility = () => { if (document.visibilityState === 'visible') check(); };

    window.addEventListener('storage', onStorage);
    window.addEventListener('focus', check);
    document.addEventListener('visibilitychange', onVisibility);
    const interval = window.setInterval(check, 15000);

    // Initial check on mount.
    check();

    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('focus', check);
      document.removeEventListener('visibilitychange', onVisibility);
      window.clearInterval(interval);
    };
  }, []);
}
